import React from "react";
import { motion } from "framer-motion";

const projects = [
  {
    projectName: "Spicejet",
    projectType: "Airlines and Aviation Industry",
    description: `SpiceJet is India's 4th largest airline, and is renowned for achieving the highest domestic passenger load factor (PLF) while operating across both domestic and international routes.`,
    image: "/SpiceJet.jpg",
    url: "https://www.spicejet.com/",
    skills: [
      "#typescript",
      "#javascript",
      "#react.js",
      "#react native",
      "#node.js",
      "#navitaire",
      "#redux",
      "#mongoDb",
      "#context api",
    ],
  },
  {
    projectName: "FMEA",
    projectType: "Failure modes and effects analysis tool",
    description: `It's a risk assessment tool used to identify, evaluate, and mitigate potential failures in a
process, product, or system before they occur.`,
    image: "/FMEA.png",
    url: "https://demo.fmea.oslabs.app/",
    skills: [
      "#javascript",
      "#typescript",
      "#react.js",
      "#ant design",
      "#redux",
      "#context api",
    ],
  },
  {
    projectName: "Drupalfit",
    projectType: "support and maintenance services tool",
    description: `DrupalFit is a subscription-based platform for Drupal support and maintenance services, assisting you in keeping your Drupal website operational while allowing you to concentrate on expanding your business.`,
    image: "/drupalfit_optimized.jpg",
    url: "https://drupalfit.com/",
    skills: [
      "#javascript",
      "#typescript",
      "#react.js",
      "#express.js",
      "#node.js",
      "#mongoDB",
      "#mongoose",
      "#serverless framework",
      "#aws sqs",
      "#aws lambda",
      "#redux",
      "#context api",
    ],
  },
  {
    projectName: "Apply Once",
    projectType: "Education related tool",
    description: `Apply Once is a single-access education portal that brings together students, institutions, financial aid providers, agents and employers and guides students all the way from courses to career opportunities.
`,
    image: "/AO_optimized.jpg",
    url: "https://www.applyonce.online",
    skills: [
      "#javascript",
      "#typescript",
      "#ant design",
      "#next js",
      "#elastic search",
      "#react.js",
      "#figma",
      "#redux",
      "#context api",
    ],
  },
  {
    projectName: "Edredo Mobile App",
    projectType: "Edtech Platform",
    description: `It's cloud-based educational platform designed to enhance online learning and teaching experiences. It offers a range of features tailored for both educators and students, aiming to foster engaging, collaborative, and personalized learning environments.`,
    image: "/edredo_optimized.jpg",
    url: "https://play.google.com/store/apps/details?id=com.edredo.edredo&pcampaignid=web_share",
    skills: [
      "#react native",
      "#x code",
      "#andriod studio",
      "#firebase",
      "#firestore",
      "#quill editor",
      "#css",
      "#javascript",
      "#figma",
    ],
  },
  {
    projectName: "AES Corporation",
    projectType: "Global Fortune 500 power company",
    description: `It's American, global energy company committed to accelerating the future of energy by 
    delivering innovative and sustainable solutions. With over four decades of experience, AES operates in 
    multiple countries, providing greener and smarter energy services to improve lives worldwide.`,
    url: "https://www.aes.com",
    image: "/aes_optimized.jpg",
    skills: ["#javascript", "#react.js", "#drupal", "#css", "#figma"],
  },
  {
    projectName: "Inspiration Cruise Travel",
    projectType: "US based Cruise Travel Company.",
    url: "https://www.inspirationtravel.com",
    description: `Inspiration is a Christian travel company that offers cruises and tours to destinations such as Alaska, the Caribbean, Israel, the USA, and the Mediterranean. They focus on faith-based travel experiences and aim to help travelers connect with God and others.`,
    image: "/ist_optimized.jpg",
    skills: ["#javascript", "#react.js", "#drupal", "#css", "#figma"],
  },
  {
    projectName: "Visit Fort Myers",
    projectType: "Travel and Tourism",
    description: `Official travel and visitor guide for Fort Myers, Florida, and its surrounding areas. It offers comprehensive information to help plan your visit, including details on local attractions, accommodations, dining options, and upcoming events.`,
    image: "/fort_optimized.jpg",
    url: "https://www.visitfortmyers.com",
    skills: [
      "#javascript",
      "#react.js",
      "#elastic search",
      "#drupal",
      "#css",
      "#figma",
      "#jQuery",
      "#context api",
    ],
  },
];

// Toggle: 'grid' | 'list' | 'bento' (featured hero + grid)
const PROJECT_LAYOUT = "bento";

const ExternalLinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

/* ─── Extracted sub-components (module-level, not re-created every render) ─── */

const ProjectGrid = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-7 lg:gap-8">
    {projects.map((project, index) => (
      <motion.a
        key={project.projectName}
        href={project.url}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.45, delay: Math.min(index * 0.07, 0.35), ease: [0.25, 1, 0.5, 1] }}
        className="group relative flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.3)] hover:border-white/20 hover:shadow-[0_24px_56px_rgba(0,0,0,0.4)] transition-all duration-300"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-white/5">
          <img loading="lazy" src={project.image} alt={project.projectName} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <span className="absolute bottom-3 right-3 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm px-3 py-1.5 text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-flex items-center gap-1.5">
            View project <ExternalLinkIcon />
          </span>
        </div>
        <div className="relative flex flex-1 flex-col p-5 sm:p-6 lg:p-7">
          <p className="text-designColor text-xs font-semibold uppercase tracking-wider mb-2">{project.projectType}</p>
          <h3 className="text-lg sm:text-xl font-bold text-white mb-2.5 group-hover:text-designColor transition-colors duration-300">{project.projectName}</h3>
          <p className="text-gray-400 text-sm leading-relaxed mb-5 flex-1">{project.description}</p>
          <div className="flex flex-wrap gap-2">
            {project.skills.slice(0, 5).map((tag, i) => (
              <span key={i} className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] sm:text-xs text-gray-400">{tag.replace(/^#/, "")}</span>
            ))}
            {project.skills.length > 5 && (
              <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] sm:text-xs text-gray-500">+{project.skills.length - 5}</span>
            )}
          </div>
        </div>
      </motion.a>
    ))}
  </div>
);

const ProjectList = () => (
  <div className="flex flex-col gap-8 lg:gap-10">
    {projects.map((project, index) => {
      const isEven = index % 2 === 0;
      return (
        <motion.a
          key={project.projectName}
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: Math.min(index * 0.06, 0.3), ease: [0.25, 1, 0.5, 1] }}
          className="group relative flex flex-col mdl:flex-row rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.3)] hover:border-white/20 hover:shadow-[0_24px_56px_rgba(0,0,0,0.4)] transition-all duration-300"
        >
          <div className={`relative w-full mdl:w-[42%] min-h-[220px] mdl:min-h-[260px] overflow-hidden bg-white/5 ${isEven ? "mdl:order-1" : "mdl:order-2"}`}>
            <img loading="lazy" src={project.image} alt={project.projectName} className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent mdl:from-transparent mdl:via-transparent mdl:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className={`absolute bottom-4 right-4 mdl:bottom-4 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm px-3 py-2 text-xs font-medium text-white inline-flex items-center gap-2 ${isEven ? "mdl:left-4 mdl:right-auto" : "mdl:right-4 mdl:left-auto"}`}>
              View project <ExternalLinkIcon />
            </div>
          </div>
          <div className={`relative flex flex-1 flex-col justify-center p-6 sm:p-8 mdl:p-10 ${isEven ? "mdl:order-2" : "mdl:order-1"}`}>
            <p className="text-designColor text-xs font-semibold uppercase tracking-wider mb-2.5">{project.projectType}</p>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-designColor transition-colors duration-300">{project.projectName}</h3>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-5">{project.description}</p>
            <div className="flex flex-wrap gap-2">
              {project.skills.slice(0, 6).map((tag, i) => (
                <span key={i} className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] sm:text-xs text-gray-400">{tag.replace(/^#/, "")}</span>
              ))}
              {project.skills.length > 6 && (
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] sm:text-xs text-gray-500">+{project.skills.length - 6}</span>
              )}
            </div>
          </div>
        </motion.a>
      );
    })}
  </div>
);

const ProjectBento = () => {
  const [featured, ...rest] = projects;
  return (
    <div className="flex flex-col gap-8 sm:gap-10 lg:gap-12">
      {/* Featured hero card */}
      <motion.a
        href={featured.url}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        whileHover={{ y: -6 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, ease: [0.25, 1, 0.5, 1] }}
        className="group relative flex flex-col lgl:flex-row rounded-[1.5rem] lgl:rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-sm overflow-hidden shadow-[0_28px_60px_rgba(0,0,0,0.4)] hover:border-designColor/30 hover:shadow-[0_32px_70px_rgba(255,1,79,0.12)] transition-all duration-400"
      >
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[radial-gradient(circle,#ff014f08,transparent_55%)] blur-2xl pointer-events-none" />
        <div className="relative w-full lgl:w-[38%] min-h-[160px] sm:min-h-[200px] lgl:min-h-[220px] overflow-hidden">
          <img
            src={featured.image}
            alt={featured.projectName}
            className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent lgl:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent lgl:hidden" />
          <span className="absolute bottom-4 left-4 lgl:left-6 rounded-full border border-white/20 bg-white/10 backdrop-blur-md px-4 py-2 text-sm font-medium text-white inline-flex items-center gap-2 shadow-lg">
            Featured · View <ExternalLinkIcon />
          </span>
        </div>
        <div className="relative flex flex-1 flex-col justify-center p-5 sm:p-6 lgl:p-8 lgl:pl-10 lgl:pr-10">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-designColor/70 via-pink-500/50 to-purple-500/50 rounded-r pointer-events-none hidden lgl:block" />
          <p className="text-white group-hover:text-designColor text-xs font-semibold uppercase tracking-wider mb-2.5 transition-colors duration-300">Featured project</p>
          <h3 className="text-2xl sm:text-3xl lgl:text-4xl font-bold text-white mb-3 group-hover:text-designColor transition-colors duration-300">
            {featured.projectName}
          </h3>
          <p className="text-white group-hover:text-designColor text-sm font-medium mb-3 transition-colors duration-300">{featured.projectType}</p>
          <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-5 max-w-xl">
            {featured.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {featured.skills.map((tag, i) => (
              <span key={i} className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] sm:text-xs text-gray-400">{tag.replace(/^#/, "")}</span>
            ))}
          </div>
        </div>
      </motion.a>

      {/* More projects — zig zag (alternating) with proper alignment */}
      <div className="relative space-y-[30px] sm:space-y-[34px]">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-widest mb-5 sm:mb-6">More projects</h3>
        {rest.map((project, index) => {
          const isEven = index % 2 === 0;
          return (
            <motion.a
              key={project.projectName}
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.2), ease: [0.25, 1, 0.5, 1] }}
              className="group block"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-6 items-stretch rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.25)] hover:border-designColor/40 hover:shadow-[0_28px_60px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,1,79,0.15)] hover:bg-white/[0.06] transition-all duration-300">
                {/* Image */}
                <div
                  className={`relative w-full min-h-[160px] sm:min-h-[180px] md:min-h-0 md:aspect-[2/1] overflow-hidden bg-white/5 flex-shrink-0 ${isEven ? "md:order-1" : "md:order-2"
                    }`}
                >
                  <img
                    loading="lazy"
                    src={project.image}
                    alt={project.projectName}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent md:from-transparent md:via-transparent md:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span
                    className={`absolute bottom-2 right-2 md:bottom-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm px-2.5 py-1.5 text-xs font-medium text-white inline-flex items-center gap-1.5 transition-all duration-300 group-hover:bg-designColor/20 group-hover:border-designColor/40 group-hover:scale-105 ${isEven ? "md:left-2 md:right-auto" : "md:right-2 md:left-auto"
                      }`}
                  >
                    View project <ExternalLinkIcon />
                  </span>
                </div>
                {/* Content */}
                <div
                  className={`flex flex-col justify-center items-start p-5 sm:p-6 text-left min-h-0 ${isEven ? "md:order-2" : "md:order-1"
                    }`}
                >
                  <p className="text-designColor text-[11px] font-semibold uppercase tracking-wider mb-2 w-full">
                    {project.projectType}
                  </p>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2.5 group-hover:text-designColor transition-colors duration-300 w-full">
                    {project.projectName}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4 w-full">{project.description}</p>
                  <div className="flex flex-wrap gap-2 w-full">
                    {project.skills.map((tag, i) => (
                      <span
                        key={i}
                        className="inline-flex rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-gray-400"
                      >
                        {tag.replace(/^#/, "")}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.a>
          );
        })}
      </div>
    </div>
  );
};

const Project = () => {
  if (PROJECT_LAYOUT === "bento") return <ProjectBento />;
  if (PROJECT_LAYOUT === "list") return <ProjectList />;
  return <ProjectGrid />;
};

export default Project;
